import React, { cloneElement, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@magoosh/lib/icons';

interface Props {
  align?: 'left' | 'right';
  children: any;
}

const Dropdown: (React.FC<Props> & { Caret: any; Chevron: any; Content: any; Trigger: any; }) = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const align = props.align || 'left';

  const closeDropdown = useRef((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setIsExpanded(false);
    }
  });

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('click', closeDropdown.current);
    } else {
      document.removeEventListener('click', closeDropdown.current);
    }

    return () => {
      document.removeEventListener('click', closeDropdown.current);
    }
  }, [isExpanded]);

  const dropdownChild = React.Children.toArray(props.children).find((child) => (
    (child as React.ReactElement<any>).type === Dropdown.Content
  )) as React.ReactElement<any>;

  const triggerChild = React.Children.toArray(props.children).find((child) => (
    (child as React.ReactElement<any>).type === Dropdown.Trigger
  )) as React.ReactElement<any>;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {
        cloneElement(
          triggerChild,
          {
            ...triggerChild.props,
            isExpanded,
            onClick: () => setIsExpanded(!isExpanded)
          }
        )
      }

      <div
        style={{
          background: '#ffffff',
          border: isExpanded ? '1px solid hsl(270, 3%, 93.5%)' : 'none',
          borderRadius: 4,
          boxShadow: isExpanded ? '1px 2px 10px 0 hsl(270, 3%, 93.5%)' : 'none',
          display: isExpanded ? 'block' : 'none',
          left: align === 'left' ? 0 : 'auto',
          marginTop: 2,
          minWidth: 160,
          overflow: 'hidden',
          position: 'absolute',
          right: align === 'right' ? 0 : 'auto',
          top: '100%',
          zIndex: 1000
        }}
      >
        <div>
          {
            cloneElement(
              dropdownChild,
              {
                ...dropdownChild.props,
                close: () => setIsExpanded(false)
              }
            )
          }
        </div>
      </div>
    </div>
  );
};

Dropdown.Caret = ({ isExpanded }) => {
  return (
    <div
      style={{
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '5px solid #B2B2B2',
        height: 0,
        width: 0
      }}
    />
  );
};

Dropdown.Chevron = ({ isExpanded }) => {
  return (
    <FontAwesomeIcon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
  );
};

Dropdown.Content = (props) => {
  if (typeof props.children === 'function') {
    return (props.children as any)({ close: props.close });
  } else {
    return props.children;
  }
};

Dropdown.Trigger = (props) => {
  return (
    <div onClick={props.onClick} style={{ cursor: 'pointer', ...(props.style || {}) }}>
      {
        (typeof props.children === 'function') ? (
          (props.children as any)({ isExpanded: props.isExpanded })
        ) : props.children
      }
    </div>
  );
};

export default Dropdown;
